import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { ReactNode, useState, useEffect, useCallback } from 'react';
import { useBusiness } from "hooks/useBusiness";
import { useLocalization } from "hooks/useLocalization";
import { Autocomplete, Paper, TextField, Box, Typography, FormControl, FormLabel, FormControlLabel, InputLabel, Select, MenuItem, Radio, RadioGroup, Switch, InputAdornment, CircularProgress, LinearProgress, Tooltip } from '@mui/material';
import {
	Lock as LockIcon
} from '@mui/icons-material';
import { useFormTemplateContext } from 'components/shared/FormFields/FormFields';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { PhoneFormField } from 'components/shared/FormFields';
import { CountryOption } from "interfaces/BusinessInterfaces";
import { get, debounce, set } from 'lodash';

type LodgingSource = 'custom' | 'google';

interface Lodging {
	id: string;
	name: string;
	address: string | null;
	coordinates: {
		lat: number | null;
		lng: number | null;
	},
	source: LodgingSource,
	isNotice?: boolean;
}

interface SelectOption {
	value: string | number;
	label: string;
	description?: string | null;
	isNotice?: boolean;
}

interface FormFieldProps<TFieldValues extends FieldValues> {
	name?: Path<TFieldValues>;
	label: string;
	type?: "text" | "select" | "radio" | "switch" | "date" | "time" | "phone" | "countryPhoneCode" | "phoneNumber" | "autocomplete" | "lodging";
	icon?: ReactNode;
	defaultValue?: string | boolean | number | null;
	disabled?: boolean;
	isLoading?: boolean;
	showLockIcon?: boolean;
	tooltip?: string;
	sx?: React.CSSProperties; // Proper type for sx
	maxLength?: number;
	required?: boolean;
	requestType?: string;
	options?: SelectOption[];
	maxOptions?: number;
	minDate?: Dayjs | null;
	maxDate?: Dayjs | null;
	onChange?: (value: any) => void;
}

export function FormField<TFieldValues extends FieldValues>({ name, label, type, icon, defaultValue, disabled, isLoading, showLockIcon, tooltip, sx, maxLength, required, requestType, options, maxOptions = 8, minDate, maxDate, onChange}: FormFieldProps<TFieldValues>) {
	const [fetchedLodgings, setFetchedLodgings] = useState<Lodging[]>([]);
	const [isFetchingLodgings, setIsFetchingLodgings] = useState(false);
	const { control, errors, isSending, disableErrorMessages } = useFormTemplateContext<TFieldValues>();
	const { countryOptions, getBizConfigVar, sendRequest, businessSlug, businessToken } = useBusiness();
	const { getStr } = useLocalization();

    let effectiveDefaultValue = defaultValue;

	if (defaultValue !== undefined) { //Default value is set
		effectiveDefaultValue = defaultValue;
	} else { //No default value specified
		switch (type) {
			case 'switch':
				effectiveDefaultValue = false;
				break;

			case 'date':
				effectiveDefaultValue = null;
				break;

			case 'countryPhoneCode':
				effectiveDefaultValue = getBizConfigVar('countryCode');
				break;

			case 'autocomplete':
				effectiveDefaultValue = null;
				break;

			default:
				effectiveDefaultValue = '';
				break;
		}
	}

	const fetchLodgings = async (query: string) => {
		if (!requestType) {
			throw new Error('Request type is required for autocomplete fields');
		}

		if (query) { //Query is not empty
			setIsFetchingLodgings(true);
	
			sendRequest({
				type: requestType,
				params: {
					query: query,
					maxResults: maxOptions
				},
				onSuccess: (response) => {
					setFetchedLodgings(response.findLodgingsByName.lodgings);
                },
				onError: (errorMessage) => {
					throw new Error(errorMessage);
				},
				onFinally: () => {
					setIsFetchingLodgings(false);
				}
			});
		} else { //Query is empty
			setFetchedLodgings([]);
		}
	};
	
	const debouncedFetchLodgings = useCallback(debounce(fetchLodgings, 300), []);

	useEffect(() => {
		return () => {
			debouncedFetchLodgings.cancel();
		};
	}, [debouncedFetchLodgings]);

	const handlePhoneNumberChange = (newPhone: string, field: any) => {
		// Remove any characters that are not digits, spaces, hyphens, parentheses, or dots
		let filteredPhone = newPhone.replace(/[^0-9 \-().]/g, '');

		if (filteredPhone !== newPhone) {
			return;
		}

		field.onChange(filteredPhone);
	};

	const filterOptions = (options: CountryOption[], { inputValue }: { inputValue: string }) => {
		return options.filter(
			option => option.title.toLowerCase().includes(inputValue.toLowerCase()) ||
				('+' + option.phoneCode).includes(inputValue)
		);
	};

	return (
		<>
			<Box display="flex" alignItems="center" sx={sx}>
				{icon && (<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{icon}</Box>)}
				{
					name === undefined ? (
						<Typography sx={sx}><span style={{ margin: '0 20px 0 10px', fontWeight: 'bold' }}>{label}</span>{defaultValue}</Typography>
					) : (
						<Controller
							name={name}
							control={control}
							defaultValue={effectiveDefaultValue as PathValue<TFieldValues, typeof name>}
							render={({ field }) => {
								switch (type) {
									case 'text':
									default:
										const textfield = 
											<TextField
												{...field}
												value={field.value}
												label={label}
												error={Boolean(errors && get(errors, name))}
												helperText={!disableErrorMessages && required && get(errors, name) ? String(get(errors, `${name}.message`)) : ''}
												required={required}
												fullWidth
												disabled={isSending || disabled}
												onChange={onChange ? (e) => onChange(e.target.value) : field.onChange} // Use custom onChange if provided
												sx={sx}
												inputProps={{ maxLength }}
												InputProps={{
													endAdornment: (
														showLockIcon ? (
															<LockIcon />
														) : (
															isLoading && (
																<InputAdornment position="end">
																	<CircularProgress size={20} />
																</InputAdornment>
															)
														)
													)
												}}
											/>;
										
											return tooltip ? (
												<Tooltip title={tooltip}>
													{textfield}
												</Tooltip>
											) : textfield;

										case 'lodging':
											const customFilterOptionsLodging = (options: Lodging[], { inputValue }: { inputValue: string }) => {
												let filtered = options;
											
												if (filtered.length === options.length && options.length > maxOptions) {
													filtered = filtered.slice(0, -1).concat({
														id: '',
														name: getStr('Continue_typing_for_more_options') + '...',
														address: null,
														coordinates: { lat: null, lng: null },
														source: 'custom',
														isNotice: true
													});
												}
											
												return filtered;
											};

											return (
												<>
													<Box position="relative" sx={{ flexGrow: 1 }}>
														<Autocomplete
															{...field}
															value={fetchedLodgings.find(option => option.id === field.value?.id) || null}
															isOptionEqualToValue={(option, value) => option.id === (value as Lodging)?.id}
															getOptionLabel={(option) => option.name}
															disabled={isSending || disabled}
															options={fetchedLodgings}
															filterOptions={customFilterOptionsLodging}
															onInputChange={(event, value) => debouncedFetchLodgings(value)}
															onChange={(event, value) => {
																if (value && (value as Lodging).isNotice) {
																	return; // Ignore the notice option
																}
																field.onChange(value ? (value as Lodging) : null);
															}}
															autoHighlight
															fullWidth
															renderOption={(props, option) => (
																<Box
																	component="li"
																	{...props}
																	key={option.id}
																	sx={{
																		display: "flex",
																		flexDirection: "column",
																		alignItems: "flex-start",
																		...(option.isNotice ? { fontStyle: "italic", color: "red", pointerEvents: "none" } : {}),
																	}}
																>
																	<Typography variant="body1" sx={{ width: "100%", textAlign: "left" }}>{option.name}</Typography>
																	<Typography variant="body2" sx={{ width: "100%", textAlign: "left" }} color="text.secondary">{ option.address }</Typography>
																</Box>
															)}
															renderInput={(params) => (
																<TextField
																	{...params}
																	label={label}
																	error={Boolean(errors && get(errors, name))}
																	required={required}
																	inputProps={{
																		...params.inputProps,
																		autoComplete: 'new-password', // disable autocomplete and autofill
																	}}
																/>
															)}
															PaperComponent={({ children }) => (
																<Paper style={{ minWidth: 300 }} elevation={3}>{children}</Paper>
															)}
														/>
														{isFetchingLodgings && (
															<LinearProgress
																sx={{
																	position: 'absolute',
																	bottom: 0,
																	left: 0,
																	right: 0
																}}
															/>
														)}
													</Box>
												</>
											);

									case 'countryPhoneCode':
										return (
											<Autocomplete
												{...field}
												value={countryOptions.find(option => option.code === field.value) || undefined}
												onChange={(event, newValue) => field.onChange(newValue ? newValue.code : '')}
												sx={{ width: '105px', marginRight: '12px' }}
												disabled={isSending || disabled}
												options={countryOptions}
												autoHighlight
												disableClearable
												filterOptions={filterOptions}
												getOptionLabel={(option) => '+' + option.phoneCode}
												renderOption={(props, option) => (
													<Box component="li" {...props} key={option.code}>
														<img
															alt={option.code}
															src={`https://www.tourtask.com/images/icons/flags/countries/flag-${option.code.toLowerCase()}-30x20.png`}
															style={{ width: 30, height: 20, marginRight: 12 }}
														/>
														<span style={{ width: '50px' }}>+{option.phoneCode}</span>
														<span>{option.title}</span>
													</Box>
												)}
												renderInput={(params) => (
													<TextField
														{...params}
														label={getStr('Country')}
														required={required}
														inputProps={{
															...params.inputProps,
															autoComplete: 'new-password', // disable autocomplete and autofill
														}}
													/>
												)}
												PaperComponent={({ children }) => (
													<Paper style={{ minWidth: 300 }} elevation={3}>{children}</Paper>
												)}

											/>
										);

									case 'phoneNumber':
										return (
											<>
												<TextField
													{...field}
													value={field.value}
													label={label}
													error={Boolean(errors && get(errors, name))}
													helperText={!disableErrorMessages && required && get(errors, name) ? String(get(errors, `${name}.message`)) : ''}
													required={required}
													disabled={isSending || disabled}
													onChange={(e) => handlePhoneNumberChange(e.target.value, field)}
													sx={sx}
													inputProps={{
														maxLength: 20
													}}
													InputProps={{
														endAdornment: (
															isLoading && (
																<InputAdornment position="end">
																	<CircularProgress size={20} />
																</InputAdornment>
															)
														)
													}}
												/>
											</>
										);
									
									case 'select':
										if (!options) throw new Error('Options are required for select fields');
										
										return (
											<FormControl fullWidth error={Boolean(required && errors && get(errors, name))}>
												<InputLabel required={required} style={{ backgroundColor: 'white', paddingLeft: '5px', paddingRight: '5px' }}>{label}</InputLabel>
												<Select
													{...field}
													error={Boolean(required && errors && get(errors, name))}
													disabled={isSending || disabled}
												>
													{options.map((option, index) => (
													<MenuItem key={index} value={option.value}>
														<div>
															<Typography>{option.label}</Typography>
															<Typography variant="body2" color="textSecondary" display="block">{option.description}</Typography>
														</div>
													</MenuItem>
													))}
												</Select>
												{!disableErrorMessages && required && errors[name] && <Typography color="error">{(errors[name] as any).message}</Typography>}
											</FormControl>
										);

									case 'radio':
										if (!options) throw new Error('options are required for radio groups');

										return (
											<FormControl
												component="fieldset"
												fullWidth
												error={Boolean(required && errors && get(errors, name))}
												sx={{ marginLeft: '14px' }}
												required={required}
												disabled={isSending}
											>
												<FormLabel component="legend">{label}</FormLabel>
												<RadioGroup {...field} row>
												{options.map((radioOption, index) => (
													<FormControlLabel
														key={index}
														control={<Radio />}
														label={radioOption.label}
														value={radioOption.value}
													/>
												))}
												</RadioGroup>
											</FormControl>
										);
										

									case 'switch':
										return (
											<FormControlLabel
												control={
													<Switch
														{...field}
														checked={field.value}
														onChange={onChange ? (e) => onChange(e.target.checked) : field.onChange} // Use custom onChange if provided
														disabled={isSending}
													/>
												}
												label={label}
												sx={{ ml: 0 }}
											/>
										);
									
									case 'date':
										return (
											<LocalizationProvider dateAdapter={AdapterDayjs}>
												<DatePicker
													label={label}
													value={field.value}
													sx={{ width: '100%' }}
													onChange={onChange ? (date) => {
														onChange(date);
													} : field.onChange}
													minDate={minDate ? minDate : undefined}
													maxDate={maxDate ? maxDate : undefined}
													disabled={isSending || disabled}
												/>
											</LocalizationProvider>
										);

									case 'time':
										return (
											<LocalizationProvider dateAdapter={AdapterDayjs}>
												<TimePicker
													label={label}
													/* value={field.value} */
													value={field.value ? field.value : null}
													onChange={onChange}
													sx={{ width: '100%' }}
													ampm={false}
													disabled={isSending || disabled}
												/>
											</LocalizationProvider>
										);
								}
							}}
						/>
					)
				}
			</Box>
		</>
	);
}